import { ClerkProvider, useAuth } from "@clerk/react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import { StrictMode, useEffect } from "react";
import { createRoot } from "react-dom/client";
import "./index.css";
import { setTokenGetter } from "./lib/auth";
import { initSentry } from "./lib/sentry";
import { routeTree } from "./routeTree.gen";

initSentry();

const queryClient = new QueryClient();
const router = createRouter({ routeTree });

// Dev-only handles for manual testing (GRC nudge cache overrides etc.).
// Gated on import.meta.env.DEV → stripped from production builds.
// See docs/manual-tests/2026-06-01-grc-nudges-manual-test.md.
if (import.meta.env.DEV) {
  (window as unknown as { qc: typeof queryClient }).qc = queryClient;
  import("./stores/gap-analysis-store").then((m) => {
    (window as unknown as { gapStore: typeof m.useGapAnalysisStore }).gapStore =
      m.useGapAnalysisStore;
  });
}

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

const CLERK_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY as
  | string
  | undefined;

/** Bridges Clerk's getToken into the module-level auth getter used by api-client and SSE hooks. */
function ClerkTokenBridge() {
  const { getToken } = useAuth();
  useEffect(() => {
    setTokenGetter(async () => getToken());
  }, [getToken]);
  return null;
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  );
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    {CLERK_KEY ? (
      <ClerkProvider publishableKey={CLERK_KEY} afterSignOutUrl="/">
        <ClerkTokenBridge />
        <App />
      </ClerkProvider>
    ) : (
      <App />
    )}
  </StrictMode>,
);
